import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import './Stack.scss';
import {StackPage, StackApi, StackPageDisplayState} from './Stack.types';
import {defer, delay} from '../../utils/Timing';

interface StackPageEx
    extends StackPage {
  displayState: StackPageDisplayState;
}

const DISPLAY_STATE_CSS_CLASSES = {
  [StackPageDisplayState.VISIBLE]:               '',
  [StackPageDisplayState.HIDDEN]:                'hidden',
  [StackPageDisplayState.ANIMATE_IN_FROM_RIGHT]: 'animate-in-from-right',
  [StackPageDisplayState.ANIMATE_IN_FROM_LEFT]:  'animate-in-from-left',
  [StackPageDisplayState.ANIMATE_OUT_TO_RIGHT]:  'animate-out-to-right',
  [StackPageDisplayState.ANIMATE_OUT_TO_LEFT]:   'animate-out-to-left'
};

/**
 * A Stack component that manages a stack of pages, allowing for push and pop operations.
 */
const Stack = forwardRef<StackApi, {}>((_, ref) => {
  const [pages, setPages] = React.useState<(StackPageEx)[]>([]);
  const [renderedPages, setRenderedPages] = React.useState<(React.ReactNode)[]>([]);

  useImperativeHandle(ref, () => ({
    replace:  (page: StackPage) => {
      replaceStackPage(page);
    },
    rootPush: (page: StackPage) => {
      rootPushStackPage(page);
    },
    push:     (page: StackPage) => {
      pushStackPage(page);
    },
    pop:      () => {
      popStackPage();
    }
  }));

  useEffect(() => {
    setRenderedPages(pages.map(page => renderStackPage(page)));
  }, [pages]);

  /**
   * Pushes a new page onto the stack. If a page with the same ID already exists, it will be replaced.
   *
   * @param {StackPage} page Page to push onto the stack.
   */
  function pushStackPage(page: StackPage) {
    if (pages.some(eachPage => eachPage.id === page.id)) {
      replaceStackPage(page);
      return;
    }

    setPages((prev) => {
      const updatedPages = [
        ...prev,
        {
          ...page,
          displayState: StackPageDisplayState.HIDDEN // Hidden by default, will be set to visible during animation
        }
      ];

      if (updatedPages.length > 1) {
        defer(() => animateInPage());
        delay(500, () => animationCompleteAfterPush());
      } else {
        defer(() => animationCompleteAfterPush());
      }

      return updatedPages;
    });
  }

  /**
   * Pushes a new page onto the stack. After the animation completes, only the previous root page and the new page will
   * be on the stack.
   *
   * @param {StackPage} page Page to push onto the stack.
   */
  function rootPushStackPage(page: StackPage) {
    if (pages.length < 2) {
      pushStackPage(page);
      return;
    }

    setPages((prev) => {
      const updatedPages = [
        { // Root page
          ...prev.at(0)!,
          displayState: StackPageDisplayState.HIDDEN
        },
        { // Previously top-most page
          ...prev.at(-1)!,
          displayState: StackPageDisplayState.ANIMATE_OUT_TO_LEFT
        },
        { // New top-most page
          ...page,
          displayState: StackPageDisplayState.ANIMATE_IN_FROM_RIGHT
        }
      ];

      delay(500, () => animationCompleteAfterRootPush());
      return updatedPages;
    });
  }

  /**
   * Pops the top page from the stack. If there is only one page, it will not be removed.
   */
  function popStackPage() {
    setPages((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      const updatedPages = [...prev];
      updatedPages.forEach(page => page.displayState = StackPageDisplayState.HIDDEN);
      updatedPages.at(-1)!.displayState = StackPageDisplayState.ANIMATE_OUT_TO_RIGHT;
      updatedPages.at(-2)!.displayState = StackPageDisplayState.ANIMATE_IN_FROM_LEFT;

      delay(500, () => animationCompleteAfterPop());
      return updatedPages;
    });
  }

  /**
   * Replaces the stack page with the same ID with the provided page. If no page with the same ID exists, nothing will
   * happen.
   *
   * @param {StackPage} page Page to replace.
   */
  function replaceStackPage(page: StackPage) {
    setPages((prev) => {
      return prev.map((eachPage) => {
        if (eachPage.id === page.id) {
          return {
            ...page,
            displayState: eachPage?.displayState
          };
        } else {
          return eachPage;
        }
      });
    });
  }

  function animateInPage() {
    setPages(prev => {
      const updatedPagesWithAnimations = prev.map(each => ({
        ...each
      }));
      updatedPagesWithAnimations.at(-1)!.displayState = StackPageDisplayState.ANIMATE_IN_FROM_RIGHT;
      updatedPagesWithAnimations.at(-2)!.displayState = StackPageDisplayState.ANIMATE_OUT_TO_LEFT;
      return updatedPagesWithAnimations;
    });
  }

  function animationCompleteAfterPush() {
    setPages(prev => prev.map((p, idx, arr) => ({
      ...p,
      displayState: idx === arr.length - 1 ? StackPageDisplayState.VISIBLE : StackPageDisplayState.HIDDEN
    })));
  }

  function animationCompleteAfterRootPush() {
    setPages(prev => {
      const updatedPages = [prev[0], prev.at(-1)!];
      updatedPages.at(-1)!.displayState = StackPageDisplayState.VISIBLE; // New page
      updatedPages.at(-2)!.displayState = StackPageDisplayState.HIDDEN; // Root page
      return updatedPages;
    });
  }

  function animationCompleteAfterPop() {
    setPages(prev => {
      const pagesAfterAnimation = prev.slice(0, -1);
      pagesAfterAnimation.forEach(page => page.displayState = StackPageDisplayState.HIDDEN);
      pagesAfterAnimation.at(-1)!.displayState = StackPageDisplayState.VISIBLE;
      return pagesAfterAnimation;
    });
  }

  /**
   * Renders a stack page.
   *
   * @param {StackPage} page The stack page to render.
   * @return {React.ReactNode} The rendered page.
   */
  function renderStackPage(page: StackPageEx): React.ReactNode {
    const cssClasses = ['stack-page', DISPLAY_STATE_CSS_CLASSES[page.displayState]].join(' ').trim();

    return <div className={cssClasses}
                key={page.id}>
      {page.node}
    </div>;
  }

  return (
      <div className="stack">
        {renderedPages}
      </div>
  );
});

export default Stack;
